import { FC, lazy, ReactNode, Suspense, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row, Space, Typography } from 'antd';
import { TbDots, TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNestedResourceAccess } from '@/hooks/useNestedResourceAccess';
import { useResourceQueryState as useQueryState } from '@/hooks/useResourceQueryState';
import { reportQuery } from '@/queries';
import {
    ChartSkeleton,
    ReportComponentActionsDropdown,
    ReportComponentCommentButton,
    ReportComponentIcon,
    ReportComponentLockButton,
    ReportComponentReadinessOverlay,
    SmallTooltip,
} from '@/shared';
import type { ReportComponentCommentResourceContext } from '@/shared/components/activity/ReportComponentCommentButton';
import { DragHandle } from '@/shared/components/drag-and-drop';
import { evaluateComponentReadiness } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/evaluateComponentReadiness';
import {
    buildReportComponentDatasetQueryConfiguration,
    getReportComponentDatasetQueryStateKey,
    getReportComponentDatasetRequestKey,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import ResourcePreviewField from '@/shared/components/resource-route-preview/ResourcePreviewField';
import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ComponentUIState } from '@/stores/database/aliases/datasets/types';
import { ReportComponent, ReportComponentType } from '@/types';

import ReportComponentDataSourceTag from '../ReportComponentDataSourceTag';

const EditReportComponentModal = lazy(
    () => import('@/shared/components/modals/ResourceActionModals/EditReportComponentModal'),
);

interface ChartContainerProps {
    component: ReportComponent;
    commentResource?: ReportComponentCommentResourceContext;
    isVisibleOnHomepage?: boolean;
    children?: ReactNode;
}

const ChartContainer: FC<ChartContainerProps> = ({ children, commentResource, isVisibleOnHomepage, component }) => {
    const { isReady, missing } = evaluateComponentReadiness(component);

    const [hovered, setHovered] = useState(false);
    const [open, setOpen] = useState(false);

    const { token } = useAntDesignTheme();
    const [reportId] = useQueryState('report', reportQuery);
    const reportAccess = useNestedResourceAccess('reports', component?.report?.id ?? reportId);
    const canModify = reportAccess.canUpdate && !component.locked;

    const onConfigure = () => {
        if (!canModify) {
            return;
        }

        setOpen(true);
    };

    const handleOpenEditor = () => {
        if (!canModify) {
            return;
        }

        setOpen(true);
    };

    const queryDatasets = useDatasetStore((state) => state.queryDatasets);
    const queryStateKey = getReportComponentDatasetQueryStateKey(component);
    const hasPublicQueryResult = Boolean(
        (component as ReportComponent & { __publicQueryResult?: unknown }).__publicQueryResult,
    );
    const loading = useDatasetStore((state) =>
        hasPublicQueryResult ? false : state.componentQueryState[queryStateKey]?.loading,
    );

    const uiState = !isReady
        ? ComponentUIState.NOT_READY
        : loading
          ? ComponentUIState.LOADING
          : ComponentUIState.READY_EMPTY;

    const queryConfiguration = useMemo(() => buildReportComponentDatasetQueryConfiguration(component), [component]);
    const requestKey = useMemo(
        () => (queryConfiguration ? getReportComponentDatasetRequestKey(queryStateKey, queryConfiguration) : undefined),
        [queryConfiguration, queryStateKey],
    );

    const handleRefreshComponent = async () => {
        if (!isReady) {
            return;
        }

        if (!queryConfiguration) {
            return;
        }

        await queryDatasets(queryStateKey, queryConfiguration, requestKey, { force: true });
    };

    useEffect(() => {
        if (hasPublicQueryResult) {
            return;
        }

        if (!isReady || !queryConfiguration || !requestKey) {
            return;
        }

        queryDatasets(queryStateKey, queryConfiguration, requestKey);
    }, [hasPublicQueryResult, isReady, queryConfiguration, queryDatasets, queryStateKey, requestKey]);

    return (
        <Card
            key={component?.id}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            styles={{
                body: {
                    padding: 0,
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
            style={{
                height: '100%',
                width: '100%',
                borderRadius: token.borderRadius,
                overflow: 'hidden',
            }}
        >
            <Row
                style={{
                    padding: '0 0.5rem',
                    flex: '0 0 3rem',
                    height: '3rem',
                    zIndex: 2,
                    borderBottom: `1px solid ${token.colorBorder}`,
                }}
                gutter={[8, 0]}
                align='middle'
                wrap={false}
            >
                {!isVisibleOnHomepage && canModify && (
                    <Col className='rgl-drag-handle-slot' flex='none'>
                        <DragHandle
                            className='rgl-drag-handle'
                            label={`Move ${component.name ?? 'report component'}`}
                        />
                    </Col>
                )}
                <Col flex='none'>
                    <SmallTooltip title={<>{component?.type}</>} placement='topLeft' padding='0.25rem 0'>
                        <ReportComponentIcon type={component.type} />
                    </SmallTooltip>
                </Col>
                <Col flex='0 1 auto' style={{ minWidth: 0 }}>
                    <Typography.Title
                        level={5}
                        style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        <ResourcePreviewField field='name' resourceType='reportComponents' resourceId={component.id}>
                            {component?.name ?? 'Untitled Component'}
                        </ResourcePreviewField>
                    </Typography.Title>
                </Col>
                <ReportComponentDataSourceTag component={component} />
                <Col flex='auto' />
                {!isVisibleOnHomepage && <ReportComponentLockButton component={component} />}
                {!isVisibleOnHomepage && hovered && (
                    <Col flex='none'>
                        <Space.Compact>
                            {/* <Button size='small' style={{ padding: '0.25rem' }}>
                                <TbSparkles style={{ color: token.purple4 }} />
                            </Button> */}
                            <SmallTooltip title='Refresh Component'>
                                <Button loading={loading} size='small' onClick={handleRefreshComponent}>
                                    <TbRefresh style={{ color: token.colorTextTertiary }} />
                                </Button>
                            </SmallTooltip>
                            <SmallTooltip title='Edit Component'>
                                <Button disabled={!canModify} size='small' onClick={handleOpenEditor}>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='0.875rem'
                                        height='0.875rem'
                                        style={{ color: token.colorTextTertiary }}
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-cash-edit'
                                    >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M7 15h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v3' />
                                        <path d='M11 19h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v1.25' />
                                        <path d='M18.42 15.61a2.1 2.1 0 1 1 2.97 2.97l-3.39 3.42h-3v-3z' />
                                    </svg>
                                </Button>
                            </SmallTooltip>
                            {reportAccess.canComment && (
                                <ReportComponentCommentButton commentResource={commentResource} component={component} />
                            )}
                            <ReportComponentActionsDropdown
                                component={component}
                                onRefresh={handleRefreshComponent}
                                refreshing={loading}
                                refreshDisabled={!isReady || !queryConfiguration}
                            >
                                <Button size='small'>
                                    <TbDots style={{ color: token.colorTextTertiary }} />
                                </Button>
                            </ReportComponentActionsDropdown>
                        </Space.Compact>
                    </Col>
                )}
            </Row>
            <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
                <div
                    style={{
                        height: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                        opacity: uiState === ComponentUIState.LOADING ? 0.25 : 1,
                    }}
                >
                    {uiState === ComponentUIState.LOADING ? <ChartSkeleton /> : children}
                </div>
                {uiState === ComponentUIState.NOT_READY && (
                    <ReportComponentReadinessOverlay
                        appearance={component.type === ReportComponentType.STATISTIC ? 'compact' : 'default'}
                        missing={missing}
                        onConfigure={onConfigure}
                        configureDisabled={!canModify || isVisibleOnHomepage}
                    />
                )}
            </div>
            {open && (
                <Suspense fallback={null}>
                    <EditReportComponentModal open={open} setOpen={setOpen} component={component} />
                </Suspense>
            )}
        </Card>
    );
};

export default ChartContainer;
